import React, { useEffect, useRef, useState } from "react";

function LazyDemoVideo({ className = "", height, label, src, width }) {
  const videoRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => {
      setPrefersReducedMotion(motionQuery.matches);
    };

    updateMotionPreference();
    motionQuery.addEventListener?.("change", updateMotionPreference);

    return () => {
      motionQuery.removeEventListener?.("change", updateMotionPreference);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" },
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !shouldLoad || prefersReducedMotion) {
      return;
    }

    const playPromise = video.play();
    playPromise?.catch?.(() => {});
  }, [prefersReducedMotion, shouldLoad]);

  return (
    <video
      ref={videoRef}
      aria-label={label}
      autoPlay={shouldLoad && !prefersReducedMotion}
      className={className}
      controls={prefersReducedMotion}
      loop
      muted
      playsInline
      preload={shouldLoad ? "metadata" : "none"}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      {shouldLoad && <source src={src} type="video/mp4" />}
      Your browser does not support embedded videos.
    </video>
  );
}

export default LazyDemoVideo;
